import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-dashboard.css";

/**
 * RAdminDashboard — /admin/dashboard
 *
 * Redesigned admin launchpad, visuals per design-ref/admin/Dashboard.html;
 * functionality replicated from the legacy
 * frontend/src/pages/AdminDashboardPage.js:
 *   - GET /api/admin/me           → header identity (AdminShell fetches it)
 *   - GET /api/admin/orders/stats → KPI row (fetched-but-unrendered in the
 *     legacy page; surfaced per ADMIN_DESIGN_HANDOFF §4 "latent UI")
 *   - GET /api/admin/database/export → DB backup with 403 super-admin gate,
 *     JSON-detail error parsing, empty-blob guard and anchor download of
 *     database_backup_YYYY-MM-DD.json
 *   - quick-action module grid linking every admin module (incl. the legacy
 *     data-testid="blackout-dates-btn")
 *   - loading spinner / "Failed to load dashboard data" error handling
 * Plus the design's recent-orders table (GET /api/admin/orders?limit=4 —
 * existing endpoint, the legacy page had an unrendered recentOrders slot).
 */

/* ---------------------------------------------------------------- utils -- */

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(amount);

/* KPI values match the design's whole-dollar figures ($48,920) */
const formatCurrencyWhole = (amount) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(amount);

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString("en-AU", {
    timeZone: "Australia/Adelaide",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/* "30 Jun 2026, 6:02 PM" — the Database card footnote format */
const formatBackupStamp = (ts) =>
  new Date(ts)
    .toLocaleString("en-AU", {
      timeZone: "Australia/Adelaide",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());

/* order helpers — same fallbacks the legacy admin pages use */
const getCustomerName = (order) => {
  if (order.customer && order.customer.first_name) {
    return `${order.customer.first_name} ${order.customer.last_name || ""}`.trim();
  }
  return order.customer_name || "Customer";
};

const getOrderTotal = (order) => {
  const total = order.total || order.total_amount || 0;
  return isNaN(total) ? 0 : total;
};

/* status → chip (real store statuses + the legacy dashboard's badge map) */
const STATUS_CHIPS = {
  new_order: { label: "New Order", cls: "adm-chip--amber" },
  shipped: { label: "Shipped", cls: "adm-chip--teal" },
  shipped_no_notification: {
    label: "Shipped (No Notification)",
    cls: "adm-chip--green-soft",
  },
  pending: { label: "Pending", cls: "adm-chip--amber" },
  confirmed: { label: "Confirmed", cls: "adm-chip--blue" },
  processing: { label: "Processing", cls: "adm-chip--purple" },
  delivered: { label: "Delivered", cls: "adm-chip--teal" },
  cancelled: { label: "Cancelled", cls: "adm-chip--red" },
};

const StatusChip = ({ status }) => {
  const info = STATUS_CHIPS[status] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : "—",
    cls: "adm-chip--grey",
  };
  return <span className={`adm-chip ${info.cls}`}>{info.label}</span>;
};

/* --------------------------------------------------------- module tiles -- */

const tileSvg = {
  viewBox: "0 0 24 24",
  width: 20,
  height: 20,
  fill: "none",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const MODULE_TILES = [
  {
    to: "/admin/orders",
    title: "Manage Orders",
    sub: "Fulfil & track orders",
    icon: (
      <svg {...tileSvg}>
        <rect x="3" y="6" width="18" height="14" rx="2" />
        <path d="M3 10h18" />
      </svg>
    ),
  },
  {
    to: "/admin/products",
    title: "Manage Products",
    sub: "Catalog & stock",
    icon: (
      <svg {...tileSvg}>
        <path d="M12 3l8 7-8 11-8-11z" />
      </svg>
    ),
  },
  {
    to: "/admin/categories",
    title: "Categories",
    sub: "Store categories",
    icon: (
      <svg {...tileSvg}>
        <path d="M20.5 12.5l-8 8-9-9V4.5h7z" />
        <circle cx="7.8" cy="7.8" r="1.4" />
      </svg>
    ),
  },
  {
    to: "/admin/bookings",
    title: "Tour Bookings",
    sub: "Manage tour bookings",
    icon: (
      <svg {...tileSvg}>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M9 6v12" strokeDasharray="2 2" />
      </svg>
    ),
  },
  {
    to: "/admin/live-dashboard",
    title: "Live Dashboard",
    sub: "Real-time tour ops",
    icon: (
      <svg {...tileSvg}>
        <circle cx="12" cy="12" r="2.5" />
        <path d="M7.5 7.5a6 6 0 000 9M16.5 7.5a6 6 0 010 9" />
      </svg>
    ),
  },
  {
    to: "/admin/users",
    title: "Admin Users",
    sub: "Roles & access",
    icon: (
      <svg {...tileSvg}>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 20a5.5 6 0 0111 0" />
        <circle cx="17" cy="9" r="2.4" />
        <path d="M15.5 20a5 5 0 016.5-4.3" />
      </svg>
    ),
  },
  {
    to: "/admin/settings",
    title: "System Settings",
    sub: "Store configuration",
    icon: (
      <svg {...tileSvg}>
        <path d="M4 6h9M18 6h2M4 12h2M11 12h9M4 18h7M16 18h4" />
        <circle cx="15.5" cy="6" r="2" />
        <circle cx="8.5" cy="12" r="2" />
        <circle cx="13.5" cy="18" r="2" />
      </svg>
    ),
  },
  {
    to: "/admin/blackout-dates",
    title: "Blackout Dates",
    sub: "Block tour dates",
    testId: "blackout-dates-btn",
    icon: (
      <svg {...tileSvg}>
        <rect x="3" y="4.5" width="18" height="16.5" rx="2" />
        <path d="M3 9.5h18M8 2.5v4M16 2.5v4M9.5 13.5l5 4M14.5 13.5l-5 4" />
      </svg>
    ),
  },
  {
    to: "/admin/analytics",
    title: "Analytics",
    sub: "Reports & exports",
    icon: (
      <svg {...tileSvg}>
        <line x1="4" y1="20" x2="4" y2="12" />
        <line x1="10" y1="20" x2="10" y2="4" />
        <line x1="16" y1="20" x2="16" y2="9" />
        <line x1="21" y1="20" x2="3" y2="20" />
      </svg>
    ),
  },
];

const LAST_BACKUP_KEY = "adminLastDbBackup";

/* ----------------------------------------------------------- component -- */

const RAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [lastBackup, setLastBackup] = useState(() => {
    const stored = Number(localStorage.getItem(LAST_BACKUP_KEY));
    return stored > 0 ? stored : null;
  });
  const navigate = useNavigate();
  const toast = useAdminToast();

  /* Same data load as the legacy fetchDashboardData(): order stats (the /me
     identity fetch lives in AdminShell), plus the design's recent orders. */
  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsResponse, ordersResponse] = await Promise.all([
        adminFetch("/api/admin/orders/stats", { navigate }),
        adminFetch("/api/admin/orders?limit=4&offset=0", { navigate }),
      ]);

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
      if (ordersResponse.ok) {
        const ordersData = await ordersResponse.json();
        setRecentOrders(Array.isArray(ordersData) ? ordersData : []);
      }
    } catch (err) {
      if (!err.isAuthError) {
        toast.error("Failed to load dashboard data");
        console.error("Dashboard error:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  /* Full database backup — identical flow to the legacy handleDatabaseBackup:
     403 super-admin gate, JSON-detail error parsing, empty-blob guard,
     anchor download named database_backup_YYYY-MM-DD.json. alert()s are
     replaced with toasts per the redesign conventions. */
  const handleDatabaseBackup = async () => {
    try {
      setBackupLoading(true);
      toast.info("⤓ Preparing database backup… download will start shortly");

      const response = await adminFetch("/api/admin/database/export", {
        navigate,
      });

      if (response.status === 403) {
        toast.error(
          "🔒 Access Denied: Super admin role required for database export",
          4000
        );
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMsg = "Failed to export database";
        try {
          const errorJson = JSON.parse(errorText);
          errorMsg = errorJson.detail || errorMsg;
        } catch (e) {
          errorMsg = errorText || errorMsg;
        }
        throw new Error(errorMsg);
      }

      // Get the blob
      const blob = await response.blob();

      // Check if we got data
      if (blob.size === 0) {
        throw new Error("Export returned empty file");
      }

      // Create download link
      const filename = `database_backup_${new Date().toISOString().split("T")[0]}.json`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Record + show success with file size
      const sizeMB = (blob.size / 1024 / 1024).toFixed(2);
      const stamp = Date.now();
      localStorage.setItem(LAST_BACKUP_KEY, String(stamp));
      setLastBackup(stamp);
      toast.success(
        `✅ Database backup downloaded — ${filename} (${sizeMB} MB)`,
        4000
      );
    } catch (err) {
      if (err.isAuthError) return; // adminFetch already redirected to login
      console.error("Backup error:", err);
      toast.error(`❌ Failed to backup database: ${err.message}`, 4000);
    } finally {
      setBackupLoading(false);
    }
  };

  /* KPI row — real values from GET /api/admin/orders/stats */
  const kpis = [
    {
      label: "Total Orders",
      value:
        stats != null ? stats.total_orders.toLocaleString("en-AU") : "—",
      icon: "adm-stat-icon--teal",
      subCls: "adm-stat-sub--teal",
      sub:
        stats != null
          ? `${stats.today_orders.toLocaleString("en-AU")} today`
          : "",
    },
    {
      label: "Total Revenue",
      value: stats != null ? formatCurrencyWhole(stats.total_revenue) : "—",
      icon: "adm-stat-icon--blue",
      subCls: "adm-stat-sub--blue",
      sub: stats != null ? `${formatCurrency(stats.today_revenue)} today` : "",
    },
    {
      label: "New Orders",
      value: stats != null ? stats.new_orders.toLocaleString("en-AU") : "—",
      icon: "adm-stat-icon--amber",
      subCls: "adm-stat-sub--amber",
      sub: stats != null ? "Awaiting fulfilment" : "",
    },
    {
      label: "Shipped Orders",
      value:
        stats != null ? stats.shipped_orders.toLocaleString("en-AU") : "—",
      icon: "adm-stat-icon--ember",
      subCls: "adm-stat-sub--ember",
      sub:
        stats != null && stats.total_orders > 0
          ? `${Math.round(
              (stats.shipped_orders / stats.total_orders) * 100
            )}% of all orders`
          : "",
    },
  ];

  return (
    <AdminShell
      active="dashboard"
      pageClass="pg-admin-dashboard"
      eyebrow="Overview"
      title="Admin Dashboard"
      actions={
        <button
          type="button"
          className="adm-iconbtn"
          onClick={fetchDashboardData}
          title="Refresh"
          aria-label="Refresh"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="#2a2018"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 11a8 8 0 10-2 6" />
            <path d="M20 4v5h-5" />
          </svg>
        </button>
      }
    >
      {loading ? (
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading dashboard...
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="adm-grid4 dash-kpis">
            {kpis.map((kpi) => (
              <div className="adm-stat" key={kpi.label}>
                <div className="adm-stat-top">
                  <span className="adm-stat-label">{kpi.label}</span>
                  <span className={`adm-stat-icon ${kpi.icon}`} />
                </div>
                <div className="adm-stat-value">{kpi.value}</div>
                {kpi.sub && (
                  <div className={`adm-stat-sub ${kpi.subCls}`}>{kpi.sub}</div>
                )}
              </div>
            ))}
          </div>

          {/* Recent orders + Database */}
          <div className="dash-mid">
            <div className="adm-card">
              <div className="dash-orders-head">
                <div className="adm-card-title">Recent orders</div>
                <Link className="adm-link" to="/admin/orders">
                  View all →
                </Link>
              </div>
              {recentOrders.length === 0 ? (
                <div className="dash-orders-empty">
                  No orders yet — orders will appear here once customers make
                  purchases.
                </div>
              ) : (
                <div className="adm-tablewrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Customer</th>
                        <th>Status</th>
                        <th>Total</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order.id || order.order_number}>
                          <td className="adm-td-strong">
                            #{order.order_number}
                          </td>
                          <td className="dash-td-customer">
                            {getCustomerName(order)}
                          </td>
                          <td>
                            <StatusChip status={order.status} />
                          </td>
                          <td className="adm-td-total">
                            {formatCurrency(getOrderTotal(order))}
                          </td>
                          <td className="adm-td-mute">
                            {formatDate(order.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="adm-card">
              <div className="adm-card-title">Database</div>
              <p className="adm-card-text">
                Export a full backup of products, orders, bookings and settings
                as JSON. Super-admin only.
              </p>
              <button
                type="button"
                className="adm-btn adm-btn--dark"
                onClick={handleDatabaseBackup}
                disabled={backupLoading}
              >
                {backupLoading ? "⤓ Exporting…" : "⤓ Full Database Backup"}
              </button>
              <div className="adm-card-foot">
                {lastBackup
                  ? `Last backup · ${formatBackupStamp(lastBackup)}`
                  : "Last backup · —"}
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="adm-card">
            <div className="adm-card-title">Quick actions</div>
            <div className="adm-grid5">
              {MODULE_TILES.map((tile) => (
                <Link
                  key={tile.to}
                  to={tile.to}
                  className="adm-tile"
                  data-testid={tile.testId}
                >
                  <span className="adm-tile-icon">{tile.icon}</span>
                  <span className="adm-tile-title">{tile.title}</span>
                  <span className="adm-tile-sub">{tile.sub}</span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </AdminShell>
  );
};

export default RAdminDashboard;
